// Import Core
import { attributes } from '../../core/ids-attributes';

// Import Utils
import { stringToBool } from '../../utils/ids-string-utils/ids-string-utils';
import { getClosestContainerNode } from '../../utils/ids-dom-utils/ids-dom-utils';
import { IdsConstructor } from '../../core/ids-element';
import { EventsMixinInterface } from '../ids-events-mixin/ids-events-mixin';

const FOCUS_CAPTURE_EVENTNAME = 'keydown.focus-capture';

type Constraints = IdsConstructor<EventsMixinInterface>;

/**
 * Doesn't allow keyboard focus to be present on elements outside of this one
 * when the `captures-focus` attribute is present.
 * @param {any} superclass Accepts a superclass and creates a new subclass from it
 * @returns {any} The extended object
 */
const IdsFocusCaptureMixin = <T extends Constraints>(superclass: T) => class extends superclass {
  constructor(...args: any[]) {
    super(...args);
  }

  static get attributes() {
    return [
      ...(superclass as any).attributes,
      attributes.AUTO_FOCUS,
      attributes.CAPTURES_FOCUS,
      attributes.CYCLES_FOCUS,
      attributes.FOCUS_INLINE
    ];
  }

  connectedCallback() {
    super.connectedCallback?.();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback?.();
    this.#removeFocusEvents();
    this.#focusableElementsInDocument = [];
  }

  /**
   * @property {boolean} capturesFocus If true, retains focus within the host component.
   * If focus is attempted on an element outside of this one, it will be "captured" and
   * placed on an element inside of this component.
   */
  #capturesFocus = false;

  /**
   * Sets whether focus should be captured within this component
   * @param {boolean | string} val true to capture focus within this component
   */
  set capturesFocus(val) {
    const currentVal = this.#capturesFocus;
    const newVal = stringToBool(val);
    if (currentVal !== newVal) {
      this.#capturesFocus = newVal;
      if (newVal) {
        this.setAttribute(attributes.CAPTURES_FOCUS, `${newVal}`);
      } else {
        this.removeAttribute(attributes.CAPTURES_FOCUS);
      }
      this.#updateFocusEvents();
    }
  }

  /**
   * Gets whether focus is captured within this component
   * @returns {boolean} true if focus is captured
   */
  get capturesFocus() {
    return this.#capturesFocus;
  }

  /**
   * @property {boolean} cyclesFocus If true, while `capturesFocus` is true, cycles the focus
   * target element to the opposite end of the container.
   */
  #cyclesFocus = true;

  /**
   * Sets whether focus should cycle between first and last focusable elements
   * @param {boolean | string} val true to enable focus cycling
   */
  set cyclesFocus(val) {
    const currentVal = this.#cyclesFocus;
    const newVal = stringToBool(val);
    if (currentVal !== newVal) {
      this.#cyclesFocus = newVal;
      if (newVal) {
        this.setAttribute(attributes.CYCLES_FOCUS, `${newVal}`);
        this.gainFocus();
      } else {
        this.removeAttribute(attributes.CYCLES_FOCUS);
      }
    }
  }

  /**
   * Gets whether focus cycles between first and last focusable elements
   * @returns {boolean} true if focus cycling is enabled
   */
  get cyclesFocus() {
    return this.#cyclesFocus;
  }

  /**
   * Connects the focus event to this component
   * @returns {void}
   */
  #attachFocusEvents() {
    const keydownEventHandler = (e: any) => {
      const isOnFirst = this.#hostNode.activeElement?.isEqualNode(this.firstFocusableElement);
      const isOnLast = this.#hostNode.activeElement?.isEqualNode(this.lastFocusableElement);

      switch (e.key) {
        case 'Tab':
          if (isOnFirst && e.shiftKey) {
            e.preventDefault();
            requestAnimationFrame(() => {
              const targetElem: any = this.cyclesFocus ? this.lastFocusableElement : this.firstFocusableElement;
              targetElem.focus();
            });
          }
          if (isOnLast && !e.shiftKey) {
            e.preventDefault();
            requestAnimationFrame(() => {
              const targetElem: any = this.cyclesFocus ? this.firstFocusableElement : this.lastFocusableElement;
              targetElem.focus();
            });
          }
          break;
        default:
          break;
      }
      return true;
    };

    // Keydown events at the document level intercept default Tab behavior on specified elements.
    // On these elements we adjust tabbing behavior.
    this.onEvent(FOCUS_CAPTURE_EVENTNAME, this.#hostNode, keydownEventHandler.bind(this));
  }

  /**
   * Disconnects the focus event from this component
   * @returns {void}
   */
  #removeFocusEvents() {
    this.offEvent(FOCUS_CAPTURE_EVENTNAME);
  }

  /**
   * Adds/Removes the focus event based on component state
   */
  #updateFocusEvents() {
    this.#removeFocusEvents();
    if (this.capturesFocus) {
      this.gainFocus();
      this.#attachFocusEvents();
    }
  }

  focusableSelectorList = [
    'button',
    'ids-button',
    'ids-dropdown',
    'ids-menu-button',
    'ids-modal-button',
    'ids-toggle-button',
    '[href]',
    'input',
    'ids-input',
    'ids-checkbox',
    'ids-radio',
    'ids-switch',
    'select',
    'textarea',
    'ids-textarea',
    '[tabindex]:not([tabindex="-1"]'
  ];

  /**
   * Gets the list of CSS selectors used to identify focusable elements
   * @returns {string[]} array of CSS selectors
   */
  get focusableSelectors() {
    return this.focusableSelectorList;
  }

  /**
   * Sets the list of CSS selectors used to identify focusable elements
   * @param {string[]} val array of CSS selectors
   */
  set focusableSelectors(val) {
    if (Array.isArray(val)) {
      this.focusableSelectorList = val;
    }
  }

  /**
   * @property {Array<HTMLElement>} focusable reference to all focusable elements on the document
   */
  #focusableElementsInDocument: any = [];

  /**
   * @readonly
   * @returns {Array<HTMLElement>} all possible focusable elements within Light DOM on the current page
   */
  get focusableElementsInDocument() {
    if (!this.#focusableElementsInDocument.length && this.focusableSelectors.length) this.refreshFocusableElements();
    return this.#focusableElementsInDocument;
  }

  /**
   * Refreshes the internal list of focusable elements in the document
   */
  refreshFocusableElements() {
    const selectorStr = this.focusableSelectors.join(', ');
    this.#focusableElementsInDocument = [...this.#hostNode.querySelectorAll(selectorStr)]
      .filter((i: HTMLElement) => !i.hasAttribute(attributes.HIDDEN));
  }

  /**
   * @readonly
   * @returns {Array<HTMLElement>} focusable elements inside of this WebComponent's Light DOM
   */
  get focusableElements() {
    if (this.focusInline) return this.focusableElementsInDocument;
    return this.focusableElementsInDocument.filter((i: any) => this.contains(i));
  }

  /**
   * @readonly
   * @returns {HTMLElement} the first focusable child element inside this component
   */
  get firstFocusableElement() {
    return this.focusableElements[0];
  }

  /**
   * @readonly
   * @returns {HTMLElement} the next focusable child element inside this component
   */
  get nextFocusableElement() {
    if (!this.focusableElements.length) {
      return undefined;
    }

    const thisIndex = this.focusableElementsInDocument.indexOf(this.#hostNode.activeElement);
    const nextElem = this.focusableElementsInDocument[thisIndex + 1];
    if (!this.contains(nextElem) && this.cyclesFocus) {
      return this.firstFocusableElement;
    }
    return nextElem;
  }

  /**
   * @readonly
   * @returns {HTMLElement} the previous focusable child element inside this component
   */
  get previousFocusableElement() {
    if (!this.focusableElements.length) {
      return undefined;
    }

    const thisIndex = this.focusableElementsInDocument.indexOf(this.#hostNode.activeElement);
    const prevElem = this.focusableElementsInDocument[thisIndex - 1];
    if (!this.contains(prevElem) && this.cyclesFocus) {
      return this.lastFocusableElement;
    }
    return prevElem;
  }

  /**
   * @readonly
   * @returns {HTMLElement} the last focusable child element inside this component
   */
  get lastFocusableElement() {
    return this.focusableElements.slice(-1)[0];
  }

  /**
   * Focuses the first-possible element within the Modal
   * @param {number|string} index the desired focusable element index to use
   * @returns {void}
   */
  setFocus(index: number | string = 0) {
    const focusable = this.focusableElements;
    const focusedEl = this.#hostNode.activeElement;
    const focusedIndex = this.focusableElements.indexOf(focusedEl);
    let safeIndex = 0;

    if (focusable.length) {
      switch (index) {
        case 'last':
          safeIndex = focusable.length - 1;
          break;
        case 'first':
        case '': // Leave at 0
          break;
        default: // undefined
          safeIndex = typeof index === 'string' ? parseInt(index) : index;
          break;
      }
      requestAnimationFrame(() => {
        focusable[safeIndex]?.focus();
      });
    }

    return (focusedIndex === safeIndex);
  }

  /**
   * Retrieves focus from elements outside of this one, and attaches
   * focus to a specified element index.
   * @param {number|string} index the desired focusable element index to use
   * @returns {void}
   */
  gainFocus(index = 0) {
    if (!this.contains(this.#hostNode.activeElement) && this.autoFocus) {
      this.setFocus(index);
    }
  }

  /*
   * The top-level node responsible for hosting focus.
   * If focusInline is true, returns this component's shadowRoot (or document as fallback).
   * Otherwise returns the closest container node (document or parent shadow root).
   */
  get #hostNode(): any {
    if (this.focusInline) {
      return this.shadowRoot || document;
    }

    return getClosestContainerNode(this);
  }

  /**
   * Sets whether focus detection should only occur within this component's shadow root
   * @param {boolean | string} val true if focus should be inline only
   */
  set focusInline(val: boolean | string | null) {
    const newValue = stringToBool(val);
    if (newValue) {
      this.setAttribute(attributes.FOCUS_INLINE, 'true');
    } else {
      this.removeAttribute(attributes.FOCUS_INLINE);
    }
  }

  /**
   * Gets whether focus detection only occurs within this component's shadow root
   * @returns {boolean} true if focus is inline only
   */
  get focusInline() {
    return this.hasAttribute(attributes.FOCUS_INLINE);
  }

  /**
   * Sets whether to focus on the first focusable element when the component is shown
   * @param {string | boolean} val value to be set as auto-focus attribute converted to boolean
   */
  set autoFocus(val: boolean | string) {
    const boolVal = stringToBool(val);
    this.setAttribute(attributes.AUTO_FOCUS, String(boolVal));
  }

  /**
   * Gets the auto-focus attribute, default is true
   * @returns {boolean} autoFocus param converted to boolean from attribute value
   */
  get autoFocus(): boolean {
    const attrVal = this.getAttribute(attributes.AUTO_FOCUS);
    if (attrVal) {
      return stringToBool(attrVal);
    }

    return true;
  }
};

export default IdsFocusCaptureMixin;
